const moment = require('moment');
const { countPlanningWorkingHours, formatPlanningStartDate, formatPlanningEndDate } = require('./planningSchedule');
const { sortPlanningEventsByMachineCategory } = require('./planningEventSort');

const DATE_FORMAT = 'YYYY-MM-DD';

function filterPlanningEvents(events = [], machines = [], category = '', machine = '') {
    if (!category && !machine) return events;
    const categoryMachines = new Set(machines
        .filter(item => item.description === category)
        .map(item => String(item.machine || '').trim()));

    return events.filter(event => {
        const selectedMachine = String(event.machineNo || '').trim() || String(event.lineNo || '').trim();
        return (!category || categoryMachines.has(selectedMachine))
            && (!machine || selectedMachine === machine);
    });
}

function createPlanningWeeks(startDate) {
    const rangeStart = moment(startDate).startOf('day');

    return [0, 1].map(index => {
        const weekStart = rangeStart.clone().add(index * 7, 'days');
        const dates = Array.from({ length: 7 }, (_, dayIndex) =>
            weekStart.clone().add(dayIndex, 'days').format(DATE_FORMAT)
        );

        return {
            index,
            startDate: dates[0],
            endDate: dates[6],
            dates,
        };
    });
}

function normalizePlanningManpower(value) {
    const manpower = Number(value);
    return Number.isFinite(manpower) ? Math.floor(manpower) : 0;
}

function getPlanningManpower(event) {
    if (!event || event.metricsPending || event.manpower == null || String(event.manpower).trim() === '' || !Number.isFinite(Number(event.manpower))) return null;
    return normalizePlanningManpower(event.manpower);
}

// Builds the planning title "M<machine> <partNo>(<manpower>)" ("L<line>" when a line is set)
// from live job data, so a changed Part No is shown instead of the saved title.
function buildPlanningTitle({ machineNo, lineNo, partNo, manpower } = {}) {
    const machine = String(machineNo || '').trim();
    const line = String(lineNo || '').trim();
    const prefix = line ? `L${line} ` : (machine ? `M${machine} ` : '');
    return `${prefix}${String(partNo || '').trim()}(${normalizePlanningManpower(manpower)})`;
}

function parsePlanningTitle(title = '') {
    const closed = /\s*\[CLOSED\]\s*$/i.test(title);
    const withoutClosed = String(title).replace(/\s*\[CLOSED\]\s*$/i, '').trim();
    const manpowerMatch = withoutClosed.match(/\(([^()]*)\)\s*$/);
    const parsedManpower = manpowerMatch ? Number(manpowerMatch[1]) : 0;

    return {
        displayTitle: manpowerMatch
            ? withoutClosed.slice(0, manpowerMatch.index).trim()
            : withoutClosed,
        manpower: normalizePlanningManpower(parsedManpower),
        closed,
    };
}

// Daily targets belong to calendar dates; working shifts still start at 07:00.
// Without a date, return the full scheduled target for the job tooltip.
function getPlanningTargetOutput(event, date) {
    const cycle = Number(event && event.cycleTime);
    if (!Number.isFinite(cycle) || cycle <= 0 || !event.start || !event.end) return 0;
    const start = moment(event.start);
    const end = moment(event.end);
    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return 0;
    if (date) {
        const day = moment(date).startOf('day');
        if (!day.isValid()) return 0;
        const overlapStart = moment.max(start, day);
        const overlapEnd = moment.min(end, day.clone().add(1, 'day'));
        if (!overlapEnd.isAfter(overlapStart)) return 0;
        const completedBy = timestamp => {
            const hours = countPlanningWorkingHours(start, timestamp, event.operationHours, false, event.ot === true, event.otSaturday === true);
            return Math.floor(Math.round(hours * 3600) / cycle);
        };
        // Carry a partial cycle into the next date so daily whole-piece targets
        // add up to the tooltip total instead of losing pieces at midnight.
        return completedBy(overlapEnd) - completedBy(overlapStart);
    }
    const hours = countPlanningWorkingHours(start, end, event.operationHours, false, event.ot === true, event.otSaturday === true);
    // Saved schedules have second precision; remove floating-point hour conversion noise.
    return Math.floor(Math.round(hours * 3600) / cycle);
}

function eventIntersectsDay(event, date) {
    const start = moment(event && event.start);
    const end = moment(event && event.end);
    if (!start.isValid() || !end.isValid()) return false;

    const dayStart = moment(date).startOf('day');
    const dayEnd = moment(date).endOf('day');

    return start.isSameOrBefore(dayEnd) && end.isSameOrAfter(dayStart);
}

function getDailyPlanningMetrics(events = [], date) {
    const sunday = moment(date).day() === 0;

    return events
        .filter(event => eventIntersectsDay(event, date)
            && (!sunday || (event.ot === true && moment(event.end).isAfter(moment(date).startOf('day')))
                || getPlanningTargetOutput(event, date) > 0))
        .reduce((totals, event) => {
            const manpower = getPlanningManpower(event);
            return {
                manpower: totals.manpower == null || manpower == null ? null : totals.manpower + manpower,
                targetOutput: totals.targetOutput + getPlanningTargetOutput(event, date),
                pending: totals.pending || event.metricsPending === true,
            };
        }, { manpower: 0, targetOutput: 0, pending: false });
}

function eventIntersectsWeek(event, week) {
    if (!event || !week) return false;
    const start = moment(event.start);
    const end = moment(event.end);
    if (!start.isValid() || !end.isValid()) return false;

    return start.isSameOrBefore(moment(week.endDate).endOf('day'))
        && end.isSameOrAfter(moment(week.startDate).startOf('day'));
}

function getPlanningWeekSummary(events = [], week) {
    const weekEvents = events.filter(event => eventIntersectsWeek(event, week));
    const uniqueIds = new Set(
        weekEvents.map(event => event.jobID ?? event.oldId ?? event.id)
    );
    const manpowerDays = week.dates.reduce(
        (total, date) => {
            const manpower = getDailyPlanningMetrics(events, date).manpower;
            return total == null || manpower == null ? null : total + manpower;
        },
        0
    );

    return {
        jobCount: uniqueIds.size,
        manpowerDays,
    };
}

function clipEventToWeek(event, week) {
    if (!event || !week || !eventIntersectsWeek(event, week)) return null;

    const weekStart = moment(week.startDate).startOf('day');
    const weekEnd = moment(week.endDate).endOf('day');
    const eventStart = moment(event.start);
    const eventEnd = moment(event.end);
    const visibleStart = eventStart.isBefore(weekStart) ? weekStart : eventStart;
    const visibleEnd = eventEnd.isAfter(weekEnd) ? weekEnd : eventEnd;

    return {
        event,
        startColumn: visibleStart.clone().startOf('day').diff(weekStart, 'days') + 1,
        endColumn: visibleEnd.clone().startOf('day').diff(weekStart, 'days') + 1,
        continuesBefore: eventStart.isBefore(weekStart),
        continuesAfter: eventEnd.isAfter(weekEnd),
    };
}

function allocatePlanningLanes(events = [], week, machines = []) {
    const segmentsByMachine = new Map();
    sortPlanningEventsByMachineCategory(events, machines).forEach(event => {
        const segment = clipEventToWeek(event, week);
        if (!segment) return;
        const selectedMachine = String(event.machineNo || '').trim() || String(event.lineNo || '').trim();
        if (!segmentsByMachine.has(selectedMachine)) segmentsByMachine.set(selectedMachine, []);
        segmentsByMachine.get(selectedMachine).push(segment);
    });

    // Reserve a contiguous block of lanes for each machine; never reuse it for another machine.
    let laneOffset = 0;
    return Array.from(segmentsByMachine.values()).flatMap(segments => {
        const laneEndColumns = [];
        const allocated = segments.sort((left, right) => (
            left.startColumn - right.startColumn
            || left.endColumn - right.endColumn
            || String(left.event.id ?? left.event.oldId ?? '').localeCompare(String(right.event.id ?? right.event.oldId ?? ''))
        ))
        .map(segment => {
            let lane = laneEndColumns.findIndex(lastColumn => segment.startColumn > lastColumn);
            if (lane === -1) lane = laneEndColumns.length;
            laneEndColumns[lane] = segment.endColumn;
            return { ...segment, lane: laneOffset + lane };
        });
        laneOffset += laneEndColumns.length;
        return allocated;
    });
}

function formatTimestampOnDate(date, sourceTimestamp) {
    const source = moment(sourceTimestamp);
    const target = moment(date);
    if (!source.isValid() || !target.isValid()) {
        throw new Error('Invalid planning timestamp');
    }
    return target.format(DATE_FORMAT) + ' ' + source.format('HH:mm:ss');
}

function movePlanningEventToDate(event, targetDate) {
    const start = moment(event && event.start);
    const end = moment(event && event.end);
    if (!start.isValid() || !end.isValid()) {
        throw new Error('Invalid planning event range');
    }

    const operationHours = Number(event.operationHours) || 24;
    const workingHours = countPlanningWorkingHours(start, end, operationHours, false, event.ot === true, event.otSaturday === true);
    if (workingHours <= 0) throw new Error('Planning event has no working duration');
    const movedStart = formatPlanningStartDate(formatTimestampOnDate(targetDate, start), true, operationHours, event.ot === true, event.otSaturday === true);

    return {
        start: movedStart,
        end: formatPlanningEndDate(movedStart, workingHours, true, operationHours, false, event.ot === true, event.otSaturday === true),
    };
}

function resizePlanningEventToDate(event, edge, targetDate) {
    const start = moment(event && event.start);
    const end = moment(event && event.end);
    if (!start.isValid() || !end.isValid()) {
        throw new Error('Invalid planning event range');
    }
    if (edge !== 'start' && edge !== 'end') {
        throw new Error('Planning resize edge must be start or end');
    }

    const resized = {
        start: start.format('YYYY-MM-DD HH:mm:ss'),
        end: end.format('YYYY-MM-DD HH:mm:ss'),
    };
    resized[edge] = formatTimestampOnDate(targetDate, edge === 'start' ? start : end);

    if (!moment(resized.end).isAfter(moment(resized.start))) {
        throw new Error('Planning end must be after start');
    }

    return resized;
}

module.exports = {
    getPlanningManpower,
    normalizePlanningManpower,
    filterPlanningEvents,
    createPlanningWeeks,
    buildPlanningTitle,
    parsePlanningTitle,
    getPlanningTargetOutput,
    eventIntersectsDay,
    getDailyPlanningMetrics,
    getPlanningWeekSummary,
    clipEventToWeek,
    allocatePlanningLanes,
    movePlanningEventToDate,
    resizePlanningEventToDate,
};
